import React, { useEffect, useState } from "react";
import FirePill from "@/components/FirePill";
import { Button } from "@/components/ui/button";

/**
 * Public preview route at /fire-preview — auto-loops a 10 → 0 countdown
 * so you can SEE the fire kick in at 7s, intensify, and shake at the
 * final 3s. Lets us iterate on the animation visually before wiring it
 * into the live BID button.
 */
export default function FirePreview() {
  const [seconds, setSeconds] = useState(10);
  const [paused, setPaused] = useState(false);
  const [manual, setManual] = useState(null); // when set, overrides loop
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (paused || manual !== null) return undefined;
    const id = setInterval(() => {
      setSeconds((s) => (s <= 0 ? 10 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [paused, manual]);

  const display = manual !== null ? manual : seconds;
  const burning = display <= 7;

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6 sm:p-12 flex flex-col items-center gap-10">
      <div className="text-center space-y-2 max-w-xl">
        <div className="text-[10px] font-mono tracking-[0.18em] text-amber-300 uppercase">
          Animation preview
        </div>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight">
          BID pill — last 7 seconds.
        </h1>
        <p className="text-sm text-zinc-400">
          Auction timers ignite at <span className="text-amber-300 font-bold">7s</span>,
          intensify as they near zero, and the pill subtly shakes for the final{" "}
          <span className="text-red-300 font-bold">3 seconds</span>. Five independently
          flickering SVG flames + drifting sparks + a pulsing heat halo + heat-
          shadow on the button — no GIFs, no videos, all CSS/SVG.
        </p>
      </div>

      {/* Live BID demo — same component you'd drop into LiveStream.jsx */}
      <div className="flex flex-col items-center gap-3">
        <div
          className={`font-mono text-5xl tabular-nums font-black tracking-tight ${
            burning ? "text-red-400" : "text-zinc-300"
          }`}
          data-testid="fire-preview-clock"
        >
          {String(display).padStart(2, "0")}
          <span className="text-base text-zinc-500 ml-2">s left</span>
        </div>

        <div className="bg-zinc-900/80 backdrop-blur border border-white/10 rounded-2xl px-4 py-5 flex items-center gap-3 mt-4">
          <div className="text-xs text-zinc-400 font-mono uppercase tracking-wider">
            $230 · 3 bids
          </div>
          <FirePill secondsLeft={display} muted={muted}>
            <Button
              className="bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full h-11 px-7 font-black text-sm tracking-wide shadow-lg shadow-[#1D78AB]/40"
              data-testid="fire-preview-bid-btn"
            >
              BID $231
            </Button>
          </FirePill>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase tracking-wider transition border ${
              muted
                ? "bg-zinc-900 text-zinc-400 border-zinc-700"
                : "bg-amber-300 text-amber-950 border-amber-200"
            }`}
            data-testid="fire-preview-mute"
          >
            {muted ? "🔇 Sound off" : "🔊 Sound on"}
          </button>
          <span className="text-[11px] text-zinc-500 leading-tight max-w-[260px]">
            Click anywhere on the page once if you don't hear anything —
            browsers require a gesture before audio can play.
          </span>
        </div>
      </div>

      {/* Manual scrub — tap any second to freeze the fire there. */}
      <div className="space-y-3 w-full max-w-md">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 text-center">
          Scrub to a specific second
        </div>
        <div className="flex justify-center flex-wrap gap-2">
          {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setManual(n)}
              className={`h-9 w-9 rounded-full text-xs font-black tabular-nums transition border ${
                manual === n
                  ? "bg-amber-300 text-amber-950 border-amber-200"
                  : n <= 7
                  ? "bg-red-500/15 text-red-300 border-red-500/30 hover:bg-red-500/25"
                  : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
              }`}
              data-testid={`fire-preview-scrub-${n}`}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => { setManual(null); setSeconds(10); }}
            className="h-9 px-3 rounded-full text-xs font-black bg-white/10 text-white border border-white/20 hover:bg-white/20"
            data-testid="fire-preview-loop"
          >
            ▶ Loop
          </button>
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            className="h-9 px-3 rounded-full text-xs font-black bg-white/10 text-white border border-white/20 hover:bg-white/20"
            disabled={manual !== null}
          >
            {paused ? "Resume" : "Pause"}
          </button>
        </div>
      </div>

      <div className="text-[11px] text-zinc-600 text-center max-w-md mt-8">
        Once you're happy with the look, I'll wire it onto the real BID button
        in <code className="text-zinc-400">LiveStream.jsx</code> — driven by the
        active listing's <code className="text-zinc-400">end_at</code> field
        ticking down. Outside the last 7 seconds the pill renders normally
        with zero added DOM.
      </div>
    </div>
  );
}
